import { useCallback, useEffect, useState } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { resolveKnownStep, sanitizeHistory } from '../utils/steps';
import type { FlowId, SelfServiceData, Step, StepId } from '../../types';
import { selfServiceApi } from '../../services/selfServiceApi';

interface UseWizardSessionBootstrapParams {
  startStep: StepId;
  resolvedFlowId: FlowId;
  routeStepId?: StepId;
  activeSteps: Partial<Record<StepId, Step>>;
  setCurrentStepId: (stepId: StepId) => void;
  replaceSelfServiceData: (data: SelfServiceData) => void;
  resetHistory: () => void;
  pushHistory: (stepId: StepId) => void;
  setSessionId: (sessionId: string | null) => void;
  navigate: NavigateFunction;
  locationSearch: string;
  navSourceRef: React.MutableRefObject<null | 'programmatic'>;
  lastStepRef: React.MutableRefObject<StepId>;
  showToast: (message: string, type?: 'success' | 'error' | 'info') => void;
  api?: typeof selfServiceApi;
}

interface RestoredState {
  selfServiceData?: SelfServiceData;
  currentStepId?: StepId;
  history?: StepId[];
}

const SUCCESS_RETURN_MESSAGES: Record<string, string> = {
  payment: 'Betaling gennemført!',
  sign: 'Signering gennemført!',
  login: 'Login gennemført!'
};

const buildStorageKey = (sessionId: string | null | undefined) => sessionId ? `wizardState:${sessionId}` : null;

const readLocalState = (sessionId: string | null | undefined): RestoredState | null => {
  const key = buildStorageKey(sessionId);
  if (!key) {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as RestoredState;
  } catch (error) {
    console.warn('Failed to read cached wizard state', error);
    return null;
  }
};

export const useWizardSessionBootstrap = ({
  startStep,
  resolvedFlowId,
  routeStepId,
  activeSteps,
  setCurrentStepId,
  replaceSelfServiceData,
  resetHistory,
  pushHistory,
  setSessionId,
  navigate,
  locationSearch,
  navSourceRef,
  lastStepRef,
  showToast,
  api = selfServiceApi
}: UseWizardSessionBootstrapParams): boolean => {
  const [isLoading, setIsLoading] = useState(true);

  const programmaticNavigate = useCallback((stepId: StepId) => {
    const path = stepId === startStep ? `/${resolvedFlowId}` : `/${resolvedFlowId}/wizard/${stepId}`;
    navSourceRef.current = 'programmatic';
    navigate({ pathname: path, search: locationSearch }, { replace: true });
    setCurrentStepId(stepId);
    lastStepRef.current = stepId;
  }, [navigate, locationSearch, resolvedFlowId, setCurrentStepId, startStep, navSourceRef, lastStepRef]);

  const createNewSession = useCallback(async () => {
    try {
      const response = await api.createSession();
      const storageKey = buildStorageKey(response.sessionId);
      if (storageKey) {
        sessionStorage.removeItem(storageKey);
      }
      setSessionId(response.sessionId);
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('sessionId', response.sessionId);
      window.history.replaceState({}, '', newUrl.toString());
      return response.sessionId;
    } catch (error) {
      console.error('Failed to create session:', error);
      showToast('Kunne ikke oprette session. Prov igen.', 'error');
      return null;
    }
  }, [api, setSessionId, showToast]);

  const applyRestoredState = useCallback((state: RestoredState | null | undefined) => {
    replaceSelfServiceData(state?.selfServiceData ?? {});
    const restoredStep = (state?.currentStepId as StepId | undefined) ?? startStep;
    setCurrentStepId(restoredStep);
    resetHistory();
    sanitizeHistory(state?.history).forEach(step => pushHistory(step));
  }, [replaceSelfServiceData, startStep, setCurrentStepId, resetHistory, pushHistory]);

  const handleReturnNavigation = useCallback((returnType: string | null, returnStatus: string | null) => {
    if (returnStatus !== 'success' || !returnType) {
      return false;
    }

    const findStep = (predicate: (stepId: StepId) => boolean): StepId | null => {
      const candidates = Object.keys(activeSteps) as StepId[];
      const match = candidates.find(predicate);
      return match ?? null;
    };

    // Create clean search params without return/status
    const url = new URLSearchParams(window.location.search);
    url.delete('return');
    url.delete('status');
    const cleanSearch = url.toString() ? `?${url.toString()}` : '';

    // Helper to navigate with clean URL
    const navigateClean = (stepId: StepId) => {
      const path = stepId === startStep ? `/${resolvedFlowId}` : `/${resolvedFlowId}/wizard/${stepId}`;
      navSourceRef.current = 'programmatic';
      navigate({ pathname: path, search: cleanSearch }, { replace: true });
      setCurrentStepId(stepId);
      lastStepRef.current = stepId;
    };

    if (returnType === 'payment') {
      const confirmationStep = findStep(stepId => stepId.endsWith('confirmation'));
      const paymentStep = findStep(stepId => stepId.includes('payment'));
      const targetStep = confirmationStep ?? startStep;

      navigateClean(targetStep);
      if (paymentStep) {
        pushHistory(paymentStep);
      }
    } else if (returnType === 'sign') {
      const previousStep = lastStepRef.current;
      const paymentStep = findStep(stepId => stepId.includes('payment'));
      const summaryStep = findStep(stepId => stepId.includes('summary'));
      const confirmationStep = findStep(stepId => stepId.includes('confirmation'));

      const targetStep = paymentStep ?? summaryStep ?? confirmationStep ?? startStep;

      if (previousStep) {
        pushHistory(previousStep);
      } else if (activeSteps['sign']) {
        pushHistory('sign');
      }

      navigateClean(targetStep);
    } else if (returnType === 'login') {
      const nextStep: StepId = 'ask_for_whom';
      navigateClean(nextStep);
      pushHistory('login');
    } else {
      return false;
    }

    const message = SUCCESS_RETURN_MESSAGES[returnType];
    if (message) {
      showToast(message, 'success');
    }
    return true;
  }, [activeSteps, navigate, pushHistory, showToast, lastStepRef, startStep, resolvedFlowId, setCurrentStepId, navSourceRef]);

  useEffect(() => {
    const initialize = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const existingSessionId = urlParams.get('bookingId') || urlParams.get('sessionId');

      if (!existingSessionId) {
        await createNewSession();
        const initialStep = resolveKnownStep(routeStepId, activeSteps, startStep);
        setCurrentStepId(initialStep);
        lastStepRef.current = initialStep;
        setIsLoading(false);
        return;
      }

      try {
        const state = await api.getState(existingSessionId);
        const localState = readLocalState(existingSessionId);

        const mergedState: RestoredState = {
          selfServiceData: {
            ...(state?.selfServiceData ?? {}),
            ...(localState?.selfServiceData ?? {})
          },
          currentStepId: localState?.currentStepId ?? state?.currentStepId,
          history: localState?.history ?? state?.history
        };

        setSessionId(existingSessionId);
        applyRestoredState(mergedState);

        const handled = handleReturnNavigation(urlParams.get('return'), urlParams.get('status'));
        if (!handled) {
          const initialStep = resolveKnownStep(routeStepId, activeSteps, startStep);
          setCurrentStepId(initialStep);
          lastStepRef.current = initialStep;
          if (urlParams.get('return') === 'success') {
            showToast('Handlingen blev gennemfort!', 'success');
          }
        }
      } catch (error) {
        console.error('Failed to restore session', error);
        showToast('Kunne ikke genoptage session', 'error');
        await createNewSession();
        setCurrentStepId(startStep);
        lastStepRef.current = startStep;
        resetHistory();
      } finally {
        setIsLoading(false);
      }
    };

    void initialize();
  }, [
    api,
    activeSteps,
    applyRestoredState,
    createNewSession,
    handleReturnNavigation,
    lastStepRef,
    routeStepId,
    setCurrentStepId,
    setSessionId,
    showToast,
    startStep
  ]);

  return isLoading;
};




